<template>
  <div class="fixed inset-0 z-50 overflow-y-auto pb-12" style="background: rgba(0, 0, 0, 0.3)"
    @click.self="close" @keydown.esc.stop.prevent="close">
    <div class="mx-auto container px-2" style="max-width: 700px">
      <div class="bg-white rounded shadow p-2 border border-gray-100 mt-12"
        role="dialog" aria-modal="true" aria-labelledby="database-search-title" data-testid="database-search">
        <div class="flex flex-row items-center">
          <p id="database-search-title" class="p-2 font-medium flex-grow text-primary text-sm">Database Search</p>
          <IconButton icon="times" class="cancel" aria-label="Close database search" @click="close" />
        </div>
        <Input ref="input" label="Search database items" placeholder="Enter an item number or part of a number"
          :value="query" @update:modelValue="updateQuery" @keydown.enter.prevent />
        <Loading v-if="searching" message="Looking up matching items..." />
        <p v-else-if="error" class="m-2 p-2 text-sm text-red-700" role="alert">{{ error }}</p>
        <template v-else-if="query.trim()">
          <p v-if="!matches.length" class="m-2 p-2 text-sm text-gray-600" role="status">No matching items found.</p>
          <div v-else class="h-80" aria-label="Matching database items">
            <DataTable :items="matches" :paginationAutoPageSize="true" :tableSettings="TABLE_SETTINGS"
              @itemSelected="onItemSelected" />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { isNavigationFailure, NavigationFailureType, useRouter } from "vue-router";
import { DataTable, IconButton, Input, Loading } from "@comp/index.js";
import { searchDatabaseItems } from "@comp/Inputs/databaseSearch.mjs";
import { typeLabel } from "@comp/Inputs/entityText.mjs";
import { appConfig, callable } from "../../db.js";

const TABLE_SETTINGS = {
  columnState: [
    {
      headerName: "Type",
      colId: "type",
      minWidth: 128,
      width: 148,
      valueGetter: (params) => typeLabel(params.data?.type),
    },
    {
      headerName: "Number",
      field: "number",
      flex: 1,
      minWidth: 140,
      cellClass: "font-medium text-primary",
    },
    {
      headerName: "Label",
      field: "label",
      flex: 2,
      minWidth: 160,
      cellClass: "text-gray-600",
    },
  ],
  rowStyle: { cursor: "pointer" },
};

const emit = defineEmits(["close"]);
const router = useRouter();
const input = ref(null);
const query = ref("");
const matches = ref([]);
const searching = ref(false);
const navigating = ref(false);
const error = ref("");
let timer = null;
let requestId = 0;
let previousFocus = null;

function invalidateSearch() {
  clearTimeout(timer);
  requestId += 1;
  matches.value = [];
  searching.value = false;
}

function close() {
  invalidateSearch();
  emit("close");
}

function updateQuery(value) {
  query.value = value;
  invalidateSearch();
  error.value = "";
  if (!value.trim()) return;
  searching.value = true;
  const current = requestId;
  timer = setTimeout(() => search(value, current), 300);
}

async function search(value, current) {
  try {
    const results = await searchDatabaseItems(value, {
      resolve: callable("resolveDatabaseItems"),
      databaseURL: appConfig.firebaseConfig.databaseURL,
    });
    if (current === requestId) matches.value = results;
  } catch (err) {
    if (current !== requestId) return;
    console.error("Database search failed:", err);
    error.value = "Could not look up matching items. Please try again.";
  } finally {
    if (current === requestId) searching.value = false;
  }
}

function onItemSelected(row) {
  const match = matches.value.find((item) => item.type === row?.data?.type && item.id === row?.data?.id);
  if (match) openMatch(match);
}

async function openMatch(match) {
  if (navigating.value || searching.value || !matches.value.includes(match)) return;
  navigating.value = true;
  try {
    const failure = await router.push(match.route);
    if (failure && !isNavigationFailure(failure, NavigationFailureType.duplicated)) {
      throw new Error("Item navigation was prevented.");
    }
    close();
  } catch (err) {
    console.error("Database search navigation failed:", err);
    error.value = "Could not open this item. Please try again.";
  } finally {
    navigating.value = false;
  }
}

onMounted(async () => {
  previousFocus = document.activeElement;
  await nextTick();
  input.value?.inputElement?.focus();
});

onBeforeUnmount(() => {
  invalidateSearch();
  previousFocus?.focus();
});
</script>
